import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Calendar,
  Clock,
  User,
  Camera,
  Video,
  Heart,
  Building,
  Star,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Shield,
  FileText,
} from 'lucide-react'

interface BookingForm {
  // Personal Information
  clientName: string
  email: string
  phone: string
  alternatePhone: string
  
  // Event Details
  eventType: string
  eventDate: string
  eventTime: string
  eventDuration: string
  venue: string
  venueAddress: string
  guestCount: string
  
  // Service Selection
  services: string[]
  packageType: string
  addOns: string[]
  
  // Special Requirements
  specialRequests: string
  dietaryRestrictions: string
  accessibilityNeeds: string
  
  // Budget
  estimatedBudget: string
  paymentMethod: string
}

interface Package {
    id: string
    name: string
    duration: string
    features: string[]
    popular?: boolean
  }

const Booking = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState<BookingForm>({
    clientName: '',
    email: '',
    phone: '',
    alternatePhone: '',
    eventType: '',
    eventDate: '',
    eventTime: '',
    eventDuration: '',
    venue: '',
    venueAddress: '',
    guestCount: '',
    services: [],
    packageType: '',
    addOns: [],
    specialRequests: '',
    dietaryRestrictions: '',
    accessibilityNeeds: '',
    estimatedBudget: '',
    paymentMethod: '',
  })
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [availableSlots, setAvailableSlots] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errors, setErrors] = useState<Partial<BookingForm>>({})
  const [currentMonth, setCurrentMonth] = useState(new Date())

  const totalSteps = 5

  const eventTypes = [
    { id: 'wedding', name: 'Wedding', icon: Heart, description: 'Complete wedding photography & videography' },
    { id: 'corporate', name: 'Corporate Event', icon: Building, description: 'Professional corporate event coverage' },
    { id: 'birthday', name: 'Birthday Party', icon: Star, description: 'Birthday celebration photography' },
    { id: 'anniversary', name: 'Anniversary', icon: Heart, description: 'Anniversary celebration coverage' },
    { id: 'engagement', name: 'Engagement', icon: Heart, description: 'Engagement ceremony photography' },
    { id: 'portrait', name: 'Portrait Session', icon: User, description: 'Professional portrait photography' },
  ]

  const services = [
    { id: 'photography', name: 'Photography', icon: Camera },
    { id: 'videography', name: 'Videography', icon: Video },
    { id: 'drone', name: 'Drone Coverage', icon: Camera },
    { id: 'livestream', name: 'Live Streaming', icon: Video },
    { id: 'editing', name: 'Professional Editing', icon: FileText },
    { id: 'album', name: 'Photo Album', icon: FileText },
  ]

  const packages: Package[] = [
    {
      id: 'basic',
      name: 'Basic Package',
      duration: '4 hours',
      features: [
        'Professional photographer',
        '200+ edited photos',
        'Online gallery',
        'Basic editing',
        '2 weeks delivery',
      ],
    },
    {
      id: 'premium',
      name: 'Premium Package',
      duration: '8 hours',
      features: [
        'Photographer + Videographer',
        '500+ edited photos',
        '3-5 minute highlight video',
        'Advanced editing',
        'Online gallery',
        'USB drive delivery',
        '1 week delivery',
      ],
      popular: true,
    },
    {
      id: 'luxury',
      name: 'Luxury Package',
      duration: 'Full day',
      features: [
        'Multiple photographers & videographers',
        '1000+ edited photos',
        'Cinematic wedding film',
        'Drone coverage',
        'Same-day highlights',
        'Premium album',
        'USB + Cloud storage',
        '3 days delivery',
      ],
    },
  ]

  const addOns = [
    { id: 'extra_hour', name: 'Extra Hour Coverage' },
    { id: 'second_photographer', name: 'Second Photographer' },
    { id: 'drone_coverage', name: 'Drone Coverage' },
    { id: 'live_streaming', name: 'Live Streaming' },
    { id: 'photo_booth', name: 'Photo Booth' },
    { id: 'premium_album', name: 'Premium Photo Album' },
  ]

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM',
    '05:00 PM', '06:00 PM', '07:00 PM', '08:00 PM',
  ]

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1)
    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - firstDay.getDay())
    
    const days = []
    const today = new Date()
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate)
      date.setDate(startDate.getDate() + i)
      
      const isCurrentMonth = date.getMonth() === month
      const isPast = date < today
      const isToday = date.toDateString() === today.toDateString()
      const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString()
      
      days.push({
        date,
        isCurrentMonth,
        isPast,
        isToday,
        isSelected,
        isAvailable: isCurrentMonth && !isPast && Math.random() > 0.3, // Simulate availability
      })
    }
    
    return days
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    if (errors[name as keyof BookingForm]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const handleServiceToggle = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }))
  }

  const handleAddOnToggle = (addOnId: string) => {
    setFormData(prev => ({
      ...prev,
      addOns: prev.addOns.includes(addOnId)
        ? prev.addOns.filter(a => a !== addOnId)
        : [...prev.addOns, addOnId]
    }))
  }

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    setFormData(prev => ({ ...prev, eventDate: date.toISOString().split('T')[0] }))
    
    // Simulate available time slots
    const slots = timeSlots.filter(() => Math.random() > 0.4)
    setAvailableSlots(slots)
  }

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<BookingForm> = {}

    switch (step) {
      case 1:
        if (!formData.clientName.trim()) newErrors.clientName = 'Name is required'
        if (!formData.email.trim()) newErrors.email = 'Email is required'
        if (!formData.phone.trim()) newErrors.phone = 'Phone is required'
        break
      case 2:
        if (!formData.eventType) newErrors.eventType = 'Event type is required'
        if (!formData.eventDate) newErrors.eventDate = 'Event date is required'
        if (!formData.eventTime) newErrors.eventTime = 'Event time is required'
        if (!formData.venue.trim()) newErrors.venue = 'Venue is required'
        break
      case 3:
        if (formData.services.length === 0) newErrors.services = ['At least one service is required'] as any
        if (!formData.packageType) newErrors.packageType = 'Package selection is required'
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps))
    }
  }

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return

    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      // Transform form data to match server expectations
      const bookingData = {
        name: formData.clientName,
        email: formData.email,
        phone: formData.phone,
        eventDate: formData.eventDate,
        selectedPackage: formData.packageType,
        additionalServices: formData.addOns,
        eventType: formData.eventType,
        specialRequirements: formData.specialRequests,
        venue: formData.venue,
        guestCount: formData.guestCount,
        estimatedBudget: formData.estimatedBudget
      }

      const response = await fetch('http://localhost:5001/api/booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      })

      if (!response.ok) {
        throw new Error('Failed to submit booking')
      }

      const result = await response.json()
      console.log('Booking submitted successfully:', result)
      setSubmitStatus('success')
    } catch (error) {
      console.error('Booking submission error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const stepTitles = [
    'Personal Information',
    'Event Details',
    'Service Selection',
    'Additional Requirements',
    'Review & Confirm',
  ]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-24"
    >
      {/* Hero Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Book Your <span className="gradient-text">Event</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/70 max-w-4xl mx-auto leading-relaxed">
              Let's capture your special moments together. Fill out the form below to get started.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Progress Bar */}
      <section className="section-padding bg-black/20">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all duration-300 ${
                      step <= currentStep
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                        : 'bg-white/20 text-white/60'
                    }`}
                  >
                    {step < currentStep ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      step
                    )}
                  </div>
                  {step < totalSteps && (
                    <div
                      className={`w-16 h-1 mx-2 transition-all duration-300 ${
                        step < currentStep
                          ? 'bg-gradient-to-r from-primary-500 to-secondary-500'
                          : 'bg-white/20'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-2">
                Step {currentStep}: {stepTitles[currentStep - 1]}
              </h2>
              <p className="text-white/70">
                {currentStep} of {totalSteps} steps completed
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Steps */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatePresence mode="wait">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="glass-card"
                >
                  <h3 className="text-xl font-bold text-white mb-6">Personal Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="clientName"
                        value={formData.clientName}
                        onChange={handleInputChange}
                        className={`glass-input w-full ${
                          errors.clientName ? 'border-red-500' : ''
                        }`}
                        placeholder="Your full name"
                      />
                      {errors.clientName && (
                        <p className="text-red-400 text-sm mt-1">{errors.clientName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`glass-input w-full ${
                          errors.email ? 'border-red-500' : ''
                        }`}
                        placeholder="your.email@example.com"
                      />
                      {errors.email && (
                        <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`glass-input w-full ${
                          errors.phone ? 'border-red-500' : ''
                        }`}
                        placeholder="+91 98765 43210"
                      />
                      {errors.phone && (
                        <p className="text-red-400 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Alternate Phone
                      </label>
                      <input
                        type="tel"
                        name="alternatePhone"
                        value={formData.alternatePhone}
                        onChange={handleInputChange}
                        className="glass-input w-full"
                        placeholder="Alternate contact number"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Event Details */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="space-y-8"
                >
                  {/* Event Type Selection */}
                  <div className="glass-card">
                    <h3 className="text-xl font-bold text-white mb-6">Event Type *</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {eventTypes.map((type) => {
                        const Icon = type.icon
                        return (
                          <button
                            key={type.id}
                            onClick={() => setFormData(prev => ({ ...prev, eventType: type.id }))}
                            className={`p-4 rounded-lg border-2 transition-all duration-300 text-left ${
                              formData.eventType === type.id
                                ? 'border-primary-500 bg-primary-500/20'
                                : 'border-white/20 hover:border-white/40 hover:bg-white/10'
                            }`}
                          >
                            <Icon className="w-8 h-8 text-primary-400 mb-3" />
                            <h4 className="text-white font-medium mb-1">{type.name}</h4>
                            <p className="text-white/70 text-sm">{type.description}</p>
                          </button>
                        )
                      })}
                    </div>
                    
                    {errors.eventType && (
                      <p className="text-red-400 text-sm mt-2">{errors.eventType}</p>
                    )}
                  </div>

                  {/* Date & Time Selection */}
                  <div className="glass-card">
                    <h3 className="text-xl font-bold text-white mb-6">Date & Time *</h3>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Calendar */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-white font-medium">Select Date</h4>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                              className="p-2 rounded-lg glass hover:bg-white/20 transition-colors duration-300"
                            >
                              <ChevronLeft className="w-4 h-4 text-white" />
                            </button>
                            <span className="text-white font-medium min-w-[120px] text-center">
                              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </span>
                            <button
                              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                              className="p-2 rounded-lg glass hover:bg-white/20 transition-colors duration-300"
                            >
                              <ChevronRight className="w-4 h-4 text-white" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-7 gap-1 mb-2">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <div key={day} className="p-2 text-center text-white/60 text-sm font-medium">
                              {day}
                            </div>
                          ))}
                        </div>
                        
                        <div className="grid grid-cols-7 gap-1">
                          {generateCalendarDays().map((day, index) => (
                            <button
                              key={index}
                              onClick={() => day.isAvailable && handleDateSelect(day.date)}
                              disabled={!day.isAvailable}
                              className={`p-2 text-sm rounded-lg transition-all duration-300 ${
                                !day.isCurrentMonth
                                  ? 'text-white/30'
                                  : day.isPast || !day.isAvailable
                                  ? 'text-white/40 cursor-not-allowed'
                                  : day.isSelected
                                  ? 'bg-primary-500 text-white'
                                  : day.isToday
                                  ? 'bg-white/20 text-white hover:bg-white/30'
                                  : 'text-white hover:bg-white/20'
                              }`}
                            >
                              {day.date.getDate()}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Time Slots */}
                      <div>
                        <h4 className="text-white font-medium mb-4">Available Time Slots</h4>
                        
                        {selectedDate ? (
                          <div className="grid grid-cols-2 gap-2">
                            {availableSlots.map((slot) => (
                              <button
                                key={slot}
                                onClick={() => setFormData(prev => ({ ...prev, eventTime: slot }))}
                                className={`p-3 rounded-lg text-sm transition-all duration-300 ${
                                  formData.eventTime === slot
                                    ? 'bg-primary-500 text-white'
                                    : 'glass hover:bg-white/20 text-white/80'
                                }`}
                              >
                                {slot}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-white/60">
                            <Clock className="w-12 h-12 mx-auto mb-2" />
                            <p>Please select a date first</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {(errors.eventDate || errors.eventTime) && (
                      <div className="mt-4 space-y-1">
                        {errors.eventDate && <p className="text-red-400 text-sm">{errors.eventDate}</p>}
                        {errors.eventTime && <p className="text-red-400 text-sm">{errors.eventTime}</p>}
                      </div>
                    )}
                  </div>

                  {/* Venue Details */}
                  <div className="glass-card">
                    <h3 className="text-xl font-bold text-white mb-6">Venue Details *</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Venue Name *
                        </label>
                        <input
                          type="text"
                          name="venue"
                          value={formData.venue}
                          onChange={handleInputChange}
                          className={`glass-input w-full ${
                            errors.venue ? 'border-red-500' : ''
                          }`}
                          placeholder="Venue name"
                        />
                        {errors.venue && (
                          <p className="text-red-400 text-sm mt-1">{errors.venue}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Guest Count
                        </label>
                        <select
                          name="guestCount"
                          value={formData.guestCount}
                          onChange={handleInputChange}
                          className="glass-input w-full"
                        >
                          <option value="">Select guest count</option>
                          <option value="1-50">1-50 guests</option>
                          <option value="51-100">51-100 guests</option>
                          <option value="101-200">101-200 guests</option>
                          <option value="201-500">201-500 guests</option>
                          <option value="500+">500+ guests</option>
                        </select>
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Venue Address
                        </label>
                        <textarea
                          name="venueAddress"
                          value={formData.venueAddress}
                          onChange={handleInputChange}
                          rows={3}
                          className="glass-input w-full resize-none"
                          placeholder="Complete venue address"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Event Duration
                        </label>
                        <select
                          name="eventDuration"
                          value={formData.eventDuration}
                          onChange={handleInputChange}
                          className="glass-input w-full"
                        >
                          <option value="">Select duration</option>
                          <option value="2-4 hours">2-4 hours</option>
                          <option value="4-6 hours">4-6 hours</option>
                          <option value="6-8 hours">6-8 hours</option>
                          <option value="8-12 hours">8-12 hours</option>
                          <option value="Full day">Full day</option>
                          <option value="Multiple days">Multiple days</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Service Selection */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="space-y-8"
                >
                  {/* Services */}
                  <div className="glass-card">
                    <h3 className="text-xl font-bold text-white mb-6">Services Required *</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {services.map((service) => {
                        const Icon = service.icon
                        const isSelected = formData.services.includes(service.id)
                        return (
                          <button
                            key={service.id}
                            onClick={() => handleServiceToggle(service.id)}
                            className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                              isSelected
                                ? 'border-primary-500 bg-primary-500/20'
                                : 'border-white/20 hover:border-white/40 hover:bg-white/10'
                            }`}
                          >
                            <Icon className="w-8 h-8 text-primary-400 mb-3 mx-auto" />
                            <h4 className="text-white font-medium text-center">{service.name}</h4>
                            {isSelected && (
                              <CheckCircle className="w-5 h-5 text-primary-400 mx-auto mt-2" />
                            )}
                          </button>
                        )
                      })}
                    </div>
                    
                    {errors.services && (
                      <p className="text-red-400 text-sm mt-2">{errors.services}</p>
                    )}
                  </div>

                  {/* Packages */}
                  <div className="glass-card">
                    <h3 className="text-xl font-bold text-white mb-6">Choose Package *</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {packages.map((pkg) => (
                        <div
                          key={pkg.id}
                          className={`relative p-6 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                            formData.packageType === pkg.id
                              ? 'border-primary-500 bg-primary-500/20'
                              : 'border-white/20 hover:border-white/40 hover:bg-white/10'
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, packageType: pkg.id }))}
                        >
                          {pkg.popular && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                              <span className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                Most Popular
                              </span>
                            </div>
                          )}
                          
                          <div className="text-center mb-4">
                            <h4 className="text-xl font-bold text-white mb-2">{pkg.name}</h4>
                            <p className="text-white/60 text-sm">{pkg.duration}</p>
                          </div>
                          
                          <ul className="space-y-2">
                            {pkg.features.map((feature, index) => (
                              <li key={index} className="flex items-center space-x-2 text-white/80 text-sm">
                                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                          
                          {formData.packageType === pkg.id && (
                            <div className="absolute top-4 right-4">
                              <CheckCircle className="w-6 h-6 text-primary-400" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {errors.packageType && (
                      <p className="text-red-400 text-sm mt-2">{errors.packageType}</p>
                    )}
                  </div>

                  {/* Add-ons */}
                  <div className="glass-card">
                    <h3 className="text-xl font-bold text-white mb-6">Add-on Services</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {addOns.map((addOn) => {
                        const isSelected = formData.addOns.includes(addOn.id)
                        return (
                          <button
                            key={addOn.id}
                            onClick={() => handleAddOnToggle(addOn.id)}
                            className={`p-4 rounded-lg border-2 transition-all duration-300 text-left ${
                              isSelected
                                ? 'border-primary-500 bg-primary-500/20'
                                : 'border-white/20 hover:border-white/40 hover:bg-white/10'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="text-white font-medium">{addOn.name}</h4>
                              </div>
                              {isSelected && (
                                <CheckCircle className="w-5 h-5 text-primary-400" />
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Additional Requirements */}
              {currentStep === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="glass-card"
                >
                  <h3 className="text-xl font-bold text-white mb-6">Additional Requirements</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Special Requests
                      </label>
                      <textarea
                        name="specialRequests"
                        value={formData.specialRequests}
                        onChange={handleInputChange}
                        rows={4}
                        className="glass-input w-full resize-none"
                        placeholder="Any special requests, specific shots you want, or important details we should know..."
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Dietary Restrictions
                        </label>
                        <textarea
                          name="dietaryRestrictions"
                          value={formData.dietaryRestrictions}
                          onChange={handleInputChange}
                          rows={3}
                          className="glass-input w-full resize-none"
                          placeholder="Any dietary restrictions for our team..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Accessibility Needs
                        </label>
                        <textarea
                          name="accessibilityNeeds"
                          value={formData.accessibilityNeeds}
                          onChange={handleInputChange}
                          rows={3}
                          className="glass-input w-full resize-none"
                          placeholder="Any accessibility requirements..."
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Estimated Budget
                        </label>
                        <select
                          name="estimatedBudget"
                          value={formData.estimatedBudget}
                          onChange={handleInputChange}
                          className="glass-input w-full"
                        >
                          <option value="">Select budget range</option>
                          <option value="small">Small Event</option>
                          <option value="medium">Medium Event</option>
                          <option value="large">Large Event</option>
                          <option value="premium">Premium Event</option>
                          <option value="luxury">Luxury Event</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-white/80 text-sm font-medium mb-2">
                          Preferred Payment Method
                        </label>
                        <select
                          name="paymentMethod"
                          value={formData.paymentMethod}
                          onChange={handleInputChange}
                          className="glass-input w-full"
                        >
                          <option value="">Select payment method</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="upi">UPI</option>
                          <option value="credit_card">Credit Card</option>
                          <option value="cash">Cash</option>
                          <option value="cheque">Cheque</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 5: Review & Confirm */}
              {currentStep === 5 && (
                <motion.div
                  key="step5"
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  className="space-y-8"
                >
                  {/* Booking Summary */}
                  <div className="glass-card">
                    <h3 className="text-xl font-bold text-white mb-6">Booking Summary</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* Client Information */}
                      <div>
                        <h4 className="text-white font-medium mb-4 flex items-center space-x-2">
                          <User className="w-5 h-5 text-primary-400" />
                          <span>Client Information</span>
                        </h4>
                        <div className="space-y-2 text-white/80">
                          <p><span className="text-white/60">Name:</span> {formData.clientName}</p>
                          <p><span className="text-white/60">Email:</span> {formData.email}</p>
                          <p><span className="text-white/60">Phone:</span> {formData.phone}</p>
                          {formData.alternatePhone && (
                            <p><span className="text-white/60">Alt Phone:</span> {formData.alternatePhone}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Event Details */}
                      <div>
                        <h4 className="text-white font-medium mb-4 flex items-center space-x-2">
                          <Calendar className="w-5 h-5 text-primary-400" />
                          <span>Event Details</span>
                        </h4>
                        <div className="space-y-2 text-white/80">
                          <p><span className="text-white/60">Type:</span> {eventTypes.find(t => t.id === formData.eventType)?.name}</p>
                          <p><span className="text-white/60">Date:</span> {formData.eventDate}</p>
                          <p><span className="text-white/60">Time:</span> {formData.eventTime}</p>
                          <p><span className="text-white/60">Duration:</span> {formData.eventDuration}</p>
                          <p><span className="text-white/60">Venue:</span> {formData.venue}</p>
                          {formData.guestCount && (
                            <p><span className="text-white/60">Guests:</span> {formData.guestCount}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Service & Pricing */}
                  <div className="glass-card">
                    <h3 className="text-xl font-bold text-white mb-6">Services & Pricing</h3>
                    
                    <div className="space-y-4">
                      {/* Selected Package */}
                      {formData.packageType && (
                        <div className="flex items-center justify-between p-4 rounded-lg bg-white/10">
                            <div>
                              <h4 className="text-white font-medium">
                                {packages.find(p => p.id === formData.packageType)?.name}
                              </h4>
                              <p className="text-white/60 text-sm">
                                {packages.find(p => p.id === formData.packageType)?.duration}
                              </p>
                            </div>
                          </div>
                      )}
                      
                      {/* Add-ons */}
                      {formData.addOns.length > 0 && (
                        <div>
                          <h4 className="text-white font-medium mb-2">Add-on Services</h4>
                          {formData.addOns.map((addOnId) => {
                            const addOn = addOns.find(a => a.id === addOnId)
                            return addOn ? (
                              <div key={addOnId} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                                <span className="text-white/80">{addOn.name}</span>
                              </div>
                            ) : null
                          })}
                        </div>
                      )}
                      
                      {/* Total */}
                      <div className="border-t border-white/20 pt-4">
                        <div className="flex items-center justify-between text-xl font-bold">
                          <span className="text-white">Pricing</span>
                          <span className="text-primary-400">To be discussed</span>
                        </div>
                        <p className="text-white/60 text-sm mt-1">*Final pricing will be provided after consultation</p>
                      </div>
                    </div>
                  </div>

                  {/* Terms & Conditions */}
                  <div className="glass-card">
                    <h3 className="text-xl font-bold text-white mb-6">Terms & Conditions</h3>
                    
                    <div className="space-y-4 text-white/80 text-sm">
                      <div className="flex items-start space-x-3">
                        <Shield className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <p>50% advance payment required to confirm booking</p>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Shield className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <p>Cancellation policy: 48 hours notice required for full refund</p>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Shield className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <p>Final edited photos/videos will be delivered within specified timeframe</p>
                      </div>
                      <div className="flex items-start space-x-3">
                        <Shield className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <p>All intellectual property rights remain with Parth Video</p>
                      </div>
                    </div>
                  </div>

                  {/* Submit Status */}
                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="glass-card border-green-500/50 bg-green-500/20"
                    >
                      <div className="flex items-center space-x-3 text-green-400">
                        <CheckCircle className="w-8 h-8" />
                        <div>
                          <h3 className="text-xl font-bold">Booking Submitted Successfully!</h3>
                          <p className="text-green-300">We'll contact you within 24 hours to confirm your booking.</p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="glass-card border-red-500/50 bg-red-500/20"
                    >
                      <div className="flex items-center space-x-3 text-red-400">
                        <AlertCircle className="w-8 h-8" />
                        <div>
                          <h3 className="text-xl font-bold">Submission Failed</h3>
                          <p className="text-red-300">Please try again or contact us directly.</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className="flex items-center space-x-2 px-6 py-3 rounded-full glass hover:bg-white/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Previous</span>
              </button>
              
              {currentStep < totalSteps ? (
                <button
                  onClick={nextStep}
                  className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full px-8 py-3 font-medium transition-all duration-300 hover:scale-105 active:scale-95"
                >
                  <span>Next</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || submitStatus === 'success'}
                  className="flex items-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full px-8 py-3 font-medium transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : submitStatus === 'success' ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>Submitted</span>
                    </>
                  ) : (
                    <>
                      <span>Confirm Booking</span>
                      <CheckCircle className="w-5 h-5" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  )
}

export default Booking